import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RequireAuth } from 'aws-auth-lib';
import PropTypes from 'prop-types';
import Loader from '../component/Loader';
import { useRole } from '../utils/RoleContext';
import { useClinic } from '../utils/ClinicContext';
import {
    getMedicalRoles,
    getRolesForClinic,
    getUniqueClinics,
    resolveStoredAccess
} from '../utils/accessSelection';
import SignOutRedirect from './SignOutRedirect';
import { getUser } from 'services/users';
import { useUser } from 'hooks/useUser';
import {
    clearAccessSelection,
    selectAccess,
    updateClinic,
    updateUser
} from 'store/userReducer';
import Logger from 'utils/logger';
import MainLayout from 'layout/MainLayout';

const CLINIC_ROLE_NAMES = ['DOCTOR', 'CLINIC_ADMIN', 'CLINIC_CARE_GIVER'];

function RequiredUser({ children }) {
    const { user } = useUser();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { role: contextRole, setRole } = useRole();
    const { setClinic } = useClinic();
    const medicalRoles = useMemo(() => getMedicalRoles(user.roles), [user.roles]);

    const { status, data } = useQuery({
        queryKey: ['user'],
        queryFn: getUser
    });

    useEffect(() => {
        if (data) dispatch(updateUser(data.data));
    }, [data, dispatch]);

    useEffect(() => {
        if (!user.uuid) return;

        if (medicalRoles.length === 0) return;

        const clearContexts = () => {
            setRole(null);
            setClinic(null);
            sessionStorage.removeItem('role');
            sessionStorage.removeItem('clinic');
        };

        if (user.selectedRole) {
            const restoredAccess = resolveStoredAccess({
                roles: medicalRoles,
                roleUuid: user.selectedRole,
                clinic: user.selectedClinic
            });

            if (!restoredAccess.isValid) {
                dispatch(clearAccessSelection());
                clearContexts();
                return;
            }

            setRole(restoredAccess.role.name);
            setClinic(restoredAccess.clinic);
            if (!restoredAccess.clinic) sessionStorage.removeItem('clinic');
            return;
        }

        if (!user.selectedClinic) {
            const clinics = getUniqueClinics(medicalRoles);

            if (clinics.length === 0) {
                if (medicalRoles.length === 1) {
                    const onlyRole = medicalRoles[0];
                    dispatch(selectAccess({ clinic: null, roleUuid: onlyRole.uuid }));
                    setRole(onlyRole.name);
                    setClinic(null);
                } else {
                    navigate('/role', { replace: true, state: { roles: medicalRoles } });
                }
                return;
            }

            if (clinics.length > 1) {
                navigate('/select-clinics', {
                    replace: true,
                    state: { clinics }
                });
                return;
            }

            const clinic = clinics[0];
            const rolesForClinic = getRolesForClinic(medicalRoles, clinic.uuid);
            if (rolesForClinic.length === 1) {
                const onlyRole = rolesForClinic[0];
                dispatch(selectAccess({ clinic, roleUuid: onlyRole.uuid }));
                setClinic(clinic);
                setRole(onlyRole.name);
            } else {
                dispatch(updateClinic(clinic));
                setClinic(clinic);
                setRole(null);
                sessionStorage.removeItem('role');
                navigate('/role', {
                    replace: true,
                    state: { roles: rolesForClinic }
                });
            }
            return;
        }

        const rolesForClinic = getRolesForClinic(medicalRoles, user.selectedClinic.uuid);
        if (rolesForClinic.length === 1) {
            const onlyRole = rolesForClinic[0];
            dispatch(
                selectAccess({ clinic: user.selectedClinic, roleUuid: onlyRole.uuid })
            );
            setClinic(user.selectedClinic);
            setRole(onlyRole.name);
        } else if (rolesForClinic.length > 1) {
            setClinic(user.selectedClinic);
            navigate('/role', {
                replace: true,
                state: { roles: rolesForClinic }
            });
        } else {
            dispatch(clearAccessSelection());
            clearContexts();
        }
    }, [
        dispatch,
        medicalRoles,
        navigate,
        setClinic,
        setRole,
        user.selectedClinic,
        user.selectedRole,
        user.uuid
    ]);

    if (status === 'error' || (user.uuid && medicalRoles.length === 0)) {
        Logger.debug('RequiredUser', 'error');
        return <SignOutRedirect />;
    }

    const selectedRole = medicalRoles.find((role) => role.uuid === user.selectedRole);
    const requiresClinic = CLINIC_ROLE_NAMES.includes(selectedRole?.name);
    const clinicMatchesRole =
        !requiresClinic ||
        (user.selectedClinic?.uuid &&
            user.selectedClinic.uuid === selectedRole?.account?.uuid);

    if (
        status === 'pending' ||
        !selectedRole ||
        contextRole !== selectedRole.name ||
        !clinicMatchesRole
    ) {
        return <Loader />;
    }

    return children;
}

RequiredUser.propTypes = {
    children: PropTypes.node.isRequired
};

function ProtectRoute() {
    const action = () => <Navigate to="/login" replace />;

    return (
        <RequireAuth action={action}>
            <RequiredUser>
                <MainLayout />
            </RequiredUser>
        </RequireAuth>
    );
}

export { ProtectRoute };
